import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PublishIcon from '@mui/icons-material/Publish';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import {
  getWorkspace,
  getVersionHistory,
  publishWorkspace,
  rollbackWorkspace,
  createDraft,
  listAssignments,
  assignWorkspace,
  deleteAssignment,
  listImportSources,
} from '../../api/workspaceApi';
import { userLabel } from '../../utils/userLabel';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function StateChip({ state }) {
  const map = {
    draft: { color: 'warning', label: 'Draft' },
    published: { color: 'success', label: 'Published' },
    archived: { color: 'default', label: 'Archived' },
  };
  const cfg = map[state] || { color: 'default', label: state };
  return <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
}

export default function WorkspaceDetailPage() {
  const { id: workspaceId } = useParams();
  const navigate = useNavigate();
  const { userData, role } = useAuth();
  const tenantId = userData?.tenantId;
  const userRole = role || userData?.role;
  const canManage = ['admin', 'superadmin', 'tenant_admin'].includes(userRole);

  const [tab, setTab] = useState(0);
  const [workspace, setWorkspace] = useState(null);
  const [versions, setVersions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [draftCreating, setDraftCreating] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ targetType: 'tenantDefault', targetId: '' });
  const [assignLoading, setAssignLoading] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [wsOutcome, versOutcome, assignOutcome] = await Promise.allSettled([
        getWorkspace(workspaceId, tenantId),
        getVersionHistory(workspaceId),
        listAssignments(workspaceId),
      ]);
      if (wsOutcome.status !== 'fulfilled') {
        throw wsOutcome.reason || new Error('Failed to load workspace');
      }
      const wsRes = wsOutcome.value;
      setWorkspace(wsRes?.data ?? wsRes);
      if (versOutcome.status === 'fulfilled') {
        const versRes = versOutcome.value;
        setVersions(
          (versRes?.data?.items ?? versRes?.items ?? []).sort(
            (a, b) => (b.versionNumber || 0) - (a.versionNumber || 0)
          )
        );
      } else {
        setVersions([]);
        console.warn('[WorkspaceDetail] Version history failed', versOutcome.reason);
      }
      if (assignOutcome.status === 'fulfilled') {
        const assignRes = assignOutcome.value;
        setAssignments(assignRes?.data?.items ?? assignRes?.items ?? []);
      } else {
        setAssignments([]);
        console.warn('[WorkspaceDetail] Assignments failed', assignOutcome.reason);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  // Publish current draft
  const handlePublish = async () => {
    if (!window.confirm('Publish the current draft? This will make it live for assigned users.')) return;
    setPublishing(true);
    try {
      await publishWorkspace(workspaceId);
      await load();
      setSnack({ severity: 'success', message: 'Draft published successfully.' });
    } catch (e) {
      setSnack({ severity: 'error', message: e?.message || 'Publish failed.' });
    } finally {
      setPublishing(false);
    }
  };

  // Create new draft
  const handleCreateDraft = async () => {
    setDraftCreating(true);
    try {
      await createDraft(workspaceId);
      await load();
      setSnack({ severity: 'success', message: 'New draft created.' });
    } catch (e) {
      setSnack({ severity: 'error', message: e?.message || 'Failed to create draft.' });
    } finally {
      setDraftCreating(false);
    }
  };

  // Rollback to a historical version
  const handleRollback = async () => {
    if (!rollbackTarget) return;
    setRollbackLoading(true);
    try {
      await rollbackWorkspace(workspaceId, rollbackTarget.id);
      setRollbackTarget(null);
      await load();
      setSnack({ severity: 'success', message: `Rollback draft created from v${rollbackTarget.versionNumber}.` });
    } catch (e) {
      setSnack({ severity: 'error', message: e?.message || 'Rollback failed.' });
    } finally {
      setRollbackLoading(false);
    }
  };

  // Load tenant users when assign dialog opens
  const handleOpenAssignDialog = useCallback(async () => {
    setAssignDialogOpen(true);
    setUsersLoading(true);
    try {
      const resp = await listImportSources();
      const items = resp?.data?.items ?? resp?.items ?? [];
      setTenantUsers(items);
    } catch {
      setTenantUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Assign workspace
  const handleAssign = async () => {
    setAssignLoading(true);
    try {
      await assignWorkspace(workspaceId, {
        targetType: assignForm.targetType,
        targetId: assignForm.targetType === 'tenantDefault' ? null : assignForm.targetId,
      });
      setAssignDialogOpen(false);
      setAssignForm({ targetType: 'tenantDefault', targetId: '' });
      await load();
      setSnack({ severity: 'success', message: 'Assignment saved.' });
    } catch (e) {
      setSnack({ severity: 'error', message: e?.message || 'Assignment failed.' });
    } finally {
      setAssignLoading(false);
    }
  };

  // Delete assignment
  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this assignment?')) return;
    try {
      await deleteAssignment(assignmentId);
      await load();
      setSnack({ severity: 'success', message: 'Assignment removed.' });
    } catch (e) {
      setSnack({ severity: 'error', message: e?.message || 'Failed to remove assignment.' });
    }
  };

  const versionColumns = [
    { field: 'versionNumber', headerName: '#', width: 60 },
    {
      field: 'state',
      headerName: 'State',
      width: 110,
      renderCell: ({ value }) => <StateChip state={value} />,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 170,
      renderCell: ({ value }) => value ? new Date(value).toLocaleString() : '—',
    },
    { field: 'createdBy', headerName: 'By', flex: 1 },
    {
      field: 'metadata',
      headerName: 'Source',
      width: 130,
      renderCell: ({ value }) => (
        <Chip label={value?.source ?? 'workspace'} size="small" variant="outlined" />
      ),
    },
    {
      field: 'rollback',
      headerName: '',
      width: 110,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.state === 'draft') return null;
        return (
          <Tooltip title={`Rollback to v${row.versionNumber}`}>
            <Button
              size="small"
              startIcon={<HistoryIcon fontSize="small" />}
              onClick={() => setRollbackTarget(row)}
              disabled={!canManage}
            >
              Rollback
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  const assignmentColumns = [
    {
      field: 'targetType',
      headerName: 'Target Type',
      width: 140,
      renderCell: ({ value }) => (
        <Chip label={value} size="small" color={value === 'tenantDefault' ? 'primary' : 'default'} variant="outlined" />
      ),
    },
    { field: 'targetId', headerName: 'Target', flex: 1, renderCell: ({ value }) => value || '—' },
    {
      field: 'assignedAt',
      headerName: 'Assigned',
      width: 170,
      renderCell: ({ value }) => value ? new Date(value).toLocaleString() : '—',
    },
    { field: 'assignedBy', headerName: 'By', flex: 1 },
    {
      field: 'delete',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Remove assignment">
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={!canManage}
              onClick={() => handleDeleteAssignment(row.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (error || !workspace) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error || 'Home designer layout not found'}</Alert>
        </Box>
      </MainLayout>
    );
  }

  const hasDraft = !!workspace.currentDraftVersionId;
  const hasPublished = !!workspace.publishedVersionId;

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
          <IconButton size="small" onClick={() => navigate('/admin/workspaces')}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="h5" fontWeight={700}>{workspace.name}</Typography>
          <Chip
            label={workspace.status}
            size="small"
            color={workspace.status === 'active' ? 'success' : 'default'}
            variant="outlined"
          />
        </Stack>
        {workspace.description && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 5, mb: 2 }}>
            {workspace.description}
          </Typography>
        )}

        {snack && (
          <Alert severity={snack.severity} sx={{ mb: 2 }} onClose={() => setSnack(null)}>
            {snack.message}
          </Alert>
        )}

        {/* Action bar */}
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Draft</Typography>
              <Typography variant="body2" fontWeight={600}>{hasDraft ? 'Pending' : 'None'}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">Published</Typography>
              <Typography variant="body2" fontWeight={600}>{hasPublished ? 'Live' : 'None'}</Typography>
            </Box>
            {workspace.publishedAt && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary">Last Published</Typography>
                  <Typography variant="body2">{new Date(workspace.publishedAt).toLocaleString()}</Typography>
                </Box>
              </>
            )}
            <Box sx={{ flex: 1 }} />
            {canManage && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<EditNoteIcon />}
                  onClick={() => navigate(`/studio/${workspaceId}`)}
                  size="small"
                  color="primary"
                >
                  Open in Home Designer
                </Button>
                <Button
                  variant="outlined"
                  startIcon={draftCreating ? <CircularProgress size={14} /> : <NoteAddIcon />}
                  disabled={draftCreating || hasDraft}
                  onClick={handleCreateDraft}
                  size="small"
                >
                  New Draft
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={publishing ? <CircularProgress size={14} /> : <PublishIcon />}
                  disabled={publishing || !hasDraft}
                  onClick={handlePublish}
                  size="small"
                >
                  Publish Draft
                </Button>
              </>
            )}
          </Stack>
        </Paper>

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
          <Tab label="Version History" />
          <Tab label={`Assignments (${assignments.length})`} />
        </Tabs>

        {/* Version History tab */}
        <TabPanel value={tab} index={0}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <DataGrid
              rows={versions}
              columns={versionColumns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              sx={{ border: 0 }}
            />
          </Paper>
        </TabPanel>

        {/* Assignments tab */}
        <TabPanel value={tab} index={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body2" color="text.secondary">
              Assignments control which users/roles see this home designer layout. Unassigned users continue using the legacy launcher.
            </Typography>
            {canManage && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenAssignDialog}
              >
                Add Assignment
              </Button>
            )}
          </Stack>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <DataGrid
              rows={assignments}
              columns={assignmentColumns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              sx={{ border: 0 }}
            />
          </Paper>
        </TabPanel>
      </Box>

      {/* Rollback confirmation dialog */}
      <Dialog open={!!rollbackTarget} onClose={() => setRollbackTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rollback to v{rollbackTarget?.versionNumber}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A new draft will be created from version {rollbackTarget?.versionNumber} (
            {rollbackTarget?.createdAt ? new Date(rollbackTarget.createdAt).toLocaleDateString() : ''}
            ). The historical version is not changed. You must publish the draft to make it live.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackTarget(null)} disabled={rollbackLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRollback}
            disabled={rollbackLoading}
            color="warning"
          >
            {rollbackLoading ? <CircularProgress size={18} /> : 'Create Rollback Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Assignment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Box>
              <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
                Target Type
              </Typography>
              <Select
                value={assignForm.targetType}
                onChange={(e) => setAssignForm((f) => ({ ...f, targetType: e.target.value, targetId: '' }))}
                fullWidth
                size="small"
              >
                <MenuItem value="tenantDefault">Tenant Default (all unassigned users)</MenuItem>
                <MenuItem value="role">Role</MenuItem>
                <MenuItem value="user">Specific User</MenuItem>
              </Select>
            </Box>
            {assignForm.targetType === 'role' && (
              <TextField
                label="Role name"
                value={assignForm.targetId}
                onChange={(e) => setAssignForm((f) => ({ ...f, targetId: e.target.value }))}
                fullWidth
                size="small"
                required
                placeholder="e.g. tenant_admin"
              />
            )}
            {assignForm.targetType === 'user' && (
              <Autocomplete
                options={tenantUsers}
                loading={usersLoading}
                getOptionLabel={(o) => userLabel(o)}
                isOptionEqualToValue={(o, v) => o.uid === v.uid}
                onChange={(_, val) => setAssignForm((f) => ({ ...f, targetId: val?.uid || '' }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select user"
                    size="small"
                    required
                    placeholder="Search by name or email"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>{usersLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>
                      ),
                    }}
                  />
                )}
              />
            )}
            <Alert severity="info" variant="outlined" sx={{ fontSize: '0.8rem' }}>
              Users without a home designer assignment will continue using the legacy launcher unchanged.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assignLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={
              assignLoading ||
              (assignForm.targetType !== 'tenantDefault' && !assignForm.targetId.trim())
            }
          >
            {assignLoading ? <CircularProgress size={18} /> : 'Save Assignment'}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
